import React, { useContext, useState } from "react";
import styled from "styled-components";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { auth, db } from "../firebase/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash, FaGoogle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";

const AdminLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);
  const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const allowOnlyAdmin = async (firebaseUser) => {
    const docRef = doc(db, "users", firebaseUser.uid);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      toast.error("Unauthorized access!");
      await signOut(auth);
      return false;
    }

    const userData = docSnap.data();
    if (userData.role !== "admin") {
      toast.error("Access denied. Not an admin user.");
      await signOut(auth);
      return false;
    }

    setUser({
      id: firebaseUser.uid,
      name: userData.name || firebaseUser.displayName || "Admin",
      email: userData.email || firebaseUser.email,
      role: userData.role,
    });
    return true;
  };

  const handleLogin = async (values, { setSubmitting }) => {
    try {
      const userCred = await signInWithEmailAndPassword(
        auth,
        values.email,
        values.password
      );

      const isAdmin = await allowOnlyAdmin(userCred.user);
      if (!isAdmin) return;

      toast.success("Admin login successful!");
      navigate("/dashboard"); // redirect to admin dashboard
    } catch (err) {
      console.error(err);
      if (err.code === "auth/user-not-found") {
        toast.error("No admin found with this email.");
      } else if (err.code === "auth/wrong-password") {
        toast.error("Incorrect password.");
      } else {
        toast.error("Login failed. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const userCred = await signInWithPopup(auth, provider);

      const isAdmin = await allowOnlyAdmin(userCred.user);
      if (!isAdmin) return;

      toast.success("Admin login successful!");
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      if (err.code === "auth/popup-closed-by-user") {
        toast.error("Google login cancelled.");
      } else {
        toast.error("Google login failed. Try again.");
      }
    }
  };

  return (
    <Container>
      <Card>
        <h2>Admin Login</h2>
        <p>Login with your admin credentials</p>

        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={validationSchema}
          onSubmit={handleLogin}
        >
          {({ isSubmitting }) => (
            <Form>
              <label>Email</label>
              <InputField
                type="email"
                name="email"
                placeholder="admin@engineercodewala.in"
              />
              <ErrorMessage name="email" component={ErrorText} />

              <label>Password</label>
              <PasswordWrapper>
                <Field
                  as={InputField}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="••••••••"
                />
                <TogglePassword
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </TogglePassword>
              </PasswordWrapper>
              <ErrorMessage name="password" component={ErrorText} />

              <LoginButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Logging in..." : "Login"}
              </LoginButton>
            </Form>
          )}
        </Formik>

        <Divider>
          <span>or</span>
        </Divider>

        <GoogleButton type="button" onClick={handleGoogleLogin}>
          <FaGoogle /> Continue with Google
        </GoogleButton>
      </Card>
    </Container>
  );
};

export default AdminLogin;

/* ---------------- Styled Components ---------------- */

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #0b0b0b, #121212);
  font-family: "Poppins", sans-serif;
  padding: 20px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 40px 32px;
  width: 400px;
  max-width: 90%;
  color: #fff;
  box-shadow: 0 10px 30px rgba(255, 122, 24, 0.25);
  backdrop-filter: blur(16px);

  h2 {
    text-align: center;
    margin-bottom: 10px;
    font-size: 28px;
    background: linear-gradient(90deg, #ff7a18, #ffb347);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    text-align: center;
    color: #aaa;
    margin-bottom: 30px;
    font-size: 15px;
  }

  label {
    font-size: 0.9rem;
    color: #ddd;
    margin-bottom: 5px;
    display: block;
  }
`;

const InputField = styled(Field)`
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 15px;
  margin-bottom: 10px;
  outline: none;
  transition: 0.3s;

  &::placeholder {
    color: #888;
  }

  &:focus {
    border: 1px solid #ff7a18;
    box-shadow: 0 0 8px rgba(255, 122, 24, 0.4);
  }
`;

const PasswordWrapper = styled.div`
  position: relative;
`;

const TogglePassword = styled.button`
  position: absolute;
  right: 14px;
  top: 12px;
  background: transparent;
  border: none;
  color: #ffb347;
  cursor: pointer;
  font-size: 18px;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 10px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  background: linear-gradient(90deg, #ff7a18, #ffb347);
  color: #fff;
  cursor: pointer;
  transition: 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 122, 24, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 22px 0;
  color: #aaa;
  font-size: 0.9rem;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.14);
  }
`;

const GoogleButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 13px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(255, 122, 24, 0.55);
    box-shadow: 0 8px 24px rgba(255, 122, 24, 0.2);
  }
`;

const ErrorText = styled.div`
  color: #ff4d4d;
  font-size: 0.85rem;
  margin-bottom: 10px;
`;
